/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useNavigate, useParams } from 'react-router-dom';
import * as color from '@constant/color';
import { ADMIN_PATH } from '@constant/admin/route-path';
import { ETheme } from '@datatype/type';
import {
  useAdminExpressionQuery,
  useDeleteExpressionMutation,
  useModifyExpressionMutation,
} from '@hook/admin/useApi';
import { queryClient } from '@config/query-client';
import { EAdminQueryKey } from '@constant/admin/query-key';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import TextButton from '@design-system/text-button';
import Spinner from '@design-system/spinner';
import Chip from '@design-system/chip';

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 16px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  box-sizing: border-box;
`;

const TopBar = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const Section = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
  padding: 20px;
  background-color: ${color.WHITE_1};
  border: 1px solid ${color.GRAY_3};
  border-radius: 8px;
`;

const Field = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 6px;
`;

const Row = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: 8px;
`;

const InputBox = styled.div`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  box-sizing: border-box;
  border: 1px solid ${color.GRAY_2};
  border-radius: 6px;
  display: flex;
  align-items: center;
`;

const NumberInput = styled.input`
  all: unset;
  width: 80px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid ${color.GRAY_2};
  border-radius: 6px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px 12px;
  border: 1px solid ${color.GRAY_2};
  border-radius: 6px;
  resize: vertical;
  font-family: inherit;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: ${color.BROWN_2};
  }
`;

const DateInput = styled.input`
  all: unset;
  height: 36px;
  padding: 0 10px;
  border: 1px solid ${color.GRAY_2};
  border-radius: 6px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  gap: 8px;
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  padding: 80px 0;
`;

const Pre = styled.pre`
  margin: 0;
  padding: 12px;
  background-color: ${color.GRAY_5};
  border-radius: 6px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 280px;
  overflow: auto;
`;

const toDateInputValue = (iso: string | null | undefined): string => {
  if (!iso) return '';
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return '';
  }
};

const ExpressionDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { expressionKey } = useParams<{ expressionKey: string }>();

  const { data, isLoading, isError, refetch } = useAdminExpressionQuery(expressionKey ?? null);
  const modifyMutation = useModifyExpressionMutation();
  const deleteMutation = useDeleteExpressionMutation();

  const expression = data?.data ?? null;

  const [transcriptionScore, setTranscriptionScore] = useState<string>('');
  const [transcriptionComment, setTranscriptionComment] = useState<string>('');
  const [keyExpressionScore, setKeyExpressionScore] = useState<string>('');
  const [keyExpressionComment, setKeyExpressionComment] = useState<string>('');
  const [publishedAt, setPublishedAt] = useState<string>('');
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (!expression) return;
    setTranscriptionScore(expression.transcriptionReviewScore?.toString() ?? '');
    setTranscriptionComment(expression.transcriptionReviewComment ?? '');
    setKeyExpressionScore(expression.keyExpressionReviewScore?.toString() ?? '');
    setKeyExpressionComment(expression.keyExpressionReviewComment ?? '');
    setPublishedAt(toDateInputValue(expression.publishedAt));
  }, [expression]);

  const sourceTitle = expression?.authenticExpressionSource?.title ?? '-';
  const platform = expression?.authenticExpressionSource?.platform ?? '-';
  const link = expression?.authenticExpressionSource?.link ?? '';

  const keyExpressionPreview = useMemo(() => {
    if (!expression) return '-';
    return (
      expression.keySegmentKo?.keyExpression ??
      expression.keySegmentEn?.keyExpression ??
      expression.keySegmentJa?.keyExpression ??
      expression.sourceKeySegment?.keyExpression ??
      '(미정)'
    );
  }, [expression]);

  const handleSave = async () => {
    if (!expressionKey) return;
    setFeedback(null);

    const parseScore = (raw: string): number | null | undefined => {
      const trimmed = raw.trim();
      if (trimmed === '') return null;
      const n = Number(trimmed);
      if (!Number.isFinite(n)) return undefined;
      return n;
    };

    const tScore = parseScore(transcriptionScore);
    const kScore = parseScore(keyExpressionScore);
    if (tScore === undefined || kScore === undefined) {
      setFeedback('점수는 숫자여야 합니다.');
      return;
    }

    let publishedAtIso: string | null = null;
    if (publishedAt) {
      const dt = new Date(publishedAt);
      if (Number.isNaN(dt.getTime())) {
        setFeedback('공개일이 올바르지 않습니다.');
        return;
      }
      publishedAtIso = dt.toISOString();
    }

    try {
      await modifyMutation.mutateAsync({
        expressionKey,
        transcriptionReviewScore: tScore,
        transcriptionReviewComment: transcriptionComment.trim() || null,
        keyExpressionReviewScore: kScore,
        keyExpressionReviewComment: keyExpressionComment.trim() || null,
        publishedAt: publishedAtIso,
      });
      setFeedback('저장되었습니다.');
      await queryClient.invalidateQueries({ queryKey: [EAdminQueryKey.ADMIN_EXPRESSIONS] });
      await refetch();
    } catch (err) {
      setFeedback('저장에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    if (!expressionKey) return;
    if (!window.confirm('정말 삭제하시겠습니까? 되돌릴 수 없습니다.')) return;
    try {
      await deleteMutation.mutateAsync(expressionKey);
      await queryClient.invalidateQueries({ queryKey: [EAdminQueryKey.ADMIN_EXPRESSIONS] });
      navigate(ADMIN_PATH.EXPRESSION_LIST_PATH, { replace: true });
    } catch (err) {
      setFeedback('삭제에 실패했습니다.');
    }
  };

  if (isLoading) {
    return (
      <Loader>
        <Spinner size='32px' color={color.BROWN_1} />
      </Loader>
    );
  }

  if (isError || !expression) {
    return (
      <Wrapper>
        <TopBar onClick={() => navigate(ADMIN_PATH.EXPRESSION_LIST_PATH)}>
          <IconBlock icon='back' design={{ icon: { size: 'small', color: color.BLACK_4 } }} />
          <TextBlock text='목록으로' design={{ text: { size: 'small', color: color.BLACK_4 } }} />
        </TopBar>
        <TextBlock
          text='데이터를 불러오지 못했습니다.'
          design={{ text: { size: 'medium', color: color.RED_2 } }}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <TopBar onClick={() => navigate(ADMIN_PATH.EXPRESSION_LIST_PATH)}>
        <IconBlock icon='back' design={{ icon: { size: 'small', color: color.BLACK_4 } }} />
        <TextBlock text='목록으로' design={{ text: { size: 'small', color: color.BLACK_4 } }} />
      </TopBar>

      <TextBlock
        text='표현 상세'
        design={{ text: { size: 'huge', color: color.BLACK_2, bold: true } }}
      />

      <Section>
        <Row>
          <Chip text={expression.languageCode} size='small' usage='static' kind='outlined' theme={ETheme.BROWN} />
          {expression.publishedAt ? (
            <Chip text='공개' size='small' usage='static' kind='filled' theme={ETheme.BLUE} />
          ) : (
            <Chip text='비공개' size='small' usage='static' kind='outlined' theme={ETheme.GRAY} />
          )}
        </Row>
        <TextBlock
          text={keyExpressionPreview}
          design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }}
        />
        <Field>
          <TextBlock text='소스' design={{ text: { size: 'small', color: color.BLACK_4 } }} />
          <TextBlock
            text={`${platform} · ${sourceTitle}`}
            design={{ text: { size: 'small', color: color.BLACK_3 } }}
          />
          {link && (
            <a href={link} target='_blank' rel='noreferrer' style={{ color: color.BROWN_1, fontSize: 13 }}>
              원본 링크 열기
            </a>
          )}
        </Field>
      </Section>

      <Section>
        <TextBlock text='검수' design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }} />

        <Field>
          <TextBlock text='전사 점수' design={{ text: { size: 'small', color: color.BLACK_4 } }} />
          <NumberInput
            type='number'
            value={transcriptionScore}
            onChange={(ev) => setTranscriptionScore(ev.target.value)}
            placeholder='0~10'
          />
        </Field>

        <Field>
          <TextBlock text='전사 코멘트' design={{ text: { size: 'small', color: color.BLACK_4 } }} />
          <TextArea
            value={transcriptionComment}
            onChange={(ev) => setTranscriptionComment(ev.target.value)}
            placeholder='검수 코멘트'
          />
        </Field>

        <Field>
          <TextBlock text='표현 점수' design={{ text: { size: 'small', color: color.BLACK_4 } }} />
          <NumberInput
            type='number'
            value={keyExpressionScore}
            onChange={(ev) => setKeyExpressionScore(ev.target.value)}
            placeholder='0~10'
          />
        </Field>

        <Field>
          <TextBlock text='표현 코멘트' design={{ text: { size: 'small', color: color.BLACK_4 } }} />
          <TextArea
            value={keyExpressionComment}
            onChange={(ev) => setKeyExpressionComment(ev.target.value)}
            placeholder='검수 코멘트'
          />
        </Field>

        <Field>
          <TextBlock text='공개일' design={{ text: { size: 'small', color: color.BLACK_4 } }} />
          <DateInput
            type='date'
            value={publishedAt}
            onChange={(ev) => setPublishedAt(ev.target.value)}
          />
        </Field>

        {feedback && (
          <TextBlock
            text={feedback}
            design={{ text: { size: 'small', color: feedback.includes('실패') ? color.RED_2 : color.GREEN_3 } }}
          />
        )}

        <ButtonRow>
          <TextButton
            text='삭제'
            theme={ETheme.RED}
            kind='outlined'
            handleClick={handleDelete}
            isLoading={deleteMutation.isPending}
          />
          <TextButton
            text='저장'
            handleClick={handleSave}
            isLoading={modifyMutation.isPending}
          />
        </ButtonRow>
      </Section>

      <Section>
        <TextBlock text='원본 데이터' design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }} />
        <Pre>{JSON.stringify(expression, null, 2)}</Pre>
      </Section>
    </Wrapper>
  );
};

export default ExpressionDetailPage;
